import { useNavigate } from "react-router-dom";
import React, { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import io from "socket.io-client";
import { useSelector, useDispatch } from "react-redux";
import {
  MdNotifications,
  MdAccountCircle,
  MdLogout,
  MdKeyboardArrowDown,
  MdMenu,
} from "react-icons/md";
import { logoutUser } from "../redux/actions/authActions";
import { FaRegUser } from "react-icons/fa";
import { toggleSidebar } from "../redux/slices/uiSlice";
import LogoutModal from "../modals/LogoutModal";
import { toast } from "react-toastify";

const Header = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [notificationDropdownOpen, setNotificationDropdownOpen] =
    useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const API_URL = process.env.REACT_APP_SOCKET_URL;
  const [socket, setSocket] = useState(null);
  const dropdownRef = useRef(null);
  const notificationRef = useRef(null);

  const getPageTitle = () => {
    switch (location.pathname) {
      case "/":
        return "Dashboard";
      case "/oem-partners":
        return "Onboarding OEM Partners";
      case "/revenue":
        return "Revenue";
      case "/transactions":
        return "Transaction";
      case "/scan":
        return "My Scan";
      case "/OEMScan":
        return "OEM Scan";
      case "/all-coupons":
        return "Coupons";
      case "/oem-myscan-list":
        return "OEM Scan Logs";
        case "/oem-user-details":
        return "OEM > Users";
      case "/profile":
        return "Profile";
      default:
        return "Dashboard";
    }
  };

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
    setDropdownOpen(false);
  };

  const confirmLogout = () => {
    // Cleanup socket connection before logout
    if (socket) {
      socket.disconnect();
      setSocket(null);
    }

    // Clear notifications
    setNotifications([]);
    setNotificationCount(0);

    dispatch(logoutUser());
    setShowLogoutModal(false);
  };

  const cancelLogout = () => {
    setShowLogoutModal(false);
  };

  const handleProfilePage = () => {
    navigate("/profile");
    setDropdownOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target)
      ) {
        setNotificationDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleNotificationClick = () => {
    setNotificationDropdownOpen(!notificationDropdownOpen);
  };

  // Handle individual notification click
  const handleNotificationItemClick = (notification, index) => {
    navigate(notification.route);

    // Remove the clicked notification
    setNotifications((prev) => prev.filter((_, i) => i !== index));
    setNotificationCount((prev) => (prev > 0 ? prev - 1 : 0));
    setNotificationDropdownOpen(false);
  };

  const getNotificationMessage = (notification) => {
    switch (notification.type) {
      case "userVerified":
        return `A new user - ${notification.data.firstName} ${notification.data.lastName} has been verified`;
      case "scanStarted":
        return `A new scan - Scan for ${notification.data.domain}`;
      default:
        return "New notification";
    }
  };

  useEffect(() => {
    const userData = JSON.parse(sessionStorage.getItem("user") || "{}");
    const almataId = userData.almataPartner?.id || user.almataPartner?.id;

    if (almataId && user?.status === 200) {
      const socketConnection = io(API_URL);
      setSocket(socketConnection);

      socketConnection.on("connect", () => {
        socketConnection.emit("joinAlmataRoom", almataId);
      });

      socketConnection.on("userVerified", (data) => {
        setNotificationCount((prev) => prev + 1);
        setNotifications((prev) => [
          { type: "userVerified", data, route: "/oem-user-details" },
          ...prev,
        ]);
        toast.success(`${data.firstName} ${data.lastName} has been verified`);
      });

      socketConnection.on("scanStarted", (data) => {
        setNotificationCount((prev) => prev + 1);
        setNotifications((prev) => [
          { type: "scanStarted", data, route: "/OEMScan" },
          ...prev,
        ]);
        toast.info(`Scan started for ${data.domain}`);
      });

      return () => {
        socketConnection.disconnect();
      };
    }
  }, [API_URL, user?.status, user.almataPartner?.id]);

  // Cleanup socket on component unmount
  useEffect(() => {
    return () => {
      if (socket) {
        socket.disconnect();
      }
    };
  }, [socket]);

  function capitalizeFirst(str = "") {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  return (
    <header className="header">
      <div className="header-left">
        <div
          className="header-toggle-btn"
          onClick={() => dispatch(toggleSidebar())}
        >
          <MdMenu />
        </div>
        <h1 className="page-title">{getPageTitle()}</h1>
      </div>
      <div className="header-right">
        <div className="header-actions">
          <div className="notification-dropdown" ref={notificationRef}>
            <div className="notification-btn" onClick={handleNotificationClick}>
              <MdNotifications />
              {notificationCount > 0 && (
                <span className="notification-badge">{notificationCount}</span>
              )}
            </div>
            {notificationDropdownOpen && (
              <div
                className="notification-dropdown-menu"
                // style={{
                //   position: "absolute",
                //   top: "100%",
                //   right: "0",
                //   width: "300px",
                //   maxHeight: "300px",
                //   overflowY: "auto",
                //   zIndex: 99999,
                //   background: "white",
                //   border: "1px solid #e5e7eb",
                //   borderRadius: "10px",
                //   boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                // }}
              >
                <div>
                  <h6
                    className="notification-header text-center text-white p-2 mb-0"
                    style={{
                      borderBottom: "2px solid #ddd",
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    }}
                  >
                    Notifications
                  </h6>
                </div>
                {notifications.length === 0 ? (
                  <div className="notification-item no-notifications">
                    No notifications
                  </div>
                ) : (
                  <div style={{ maxHeight: "250px", overflowY: "auto" }}>
                    {notifications.map((notification, index) => (
                      <div
                        key={index}
                        className="notification-item"
                        onClick={() =>
                          handleNotificationItemClick(notification, index)
                        }
                      >
                        <span className="notification_active"></span>{" "}
                        {getNotificationMessage(notification)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="profile-dropdown" ref={dropdownRef}>
            <div
              className="profile-btn"
              onClick={() => setDropdownOpen(!dropdownOpen)}
            >
              {/* <MdAccountCircle /> */}
              <span className="user-name-header">
                {/* {user?.almataPartner?.companyName ||
                  user?.companyName ||
                  user?.name ||
                  "Almata Partner"} */}
                {capitalizeFirst(
                  user?.almataPartner?.companyName ||
                    user?.companyName ||
                    user?.name ||
                    "Almata Partner"
                )}
              </span>
              <MdKeyboardArrowDown
                className={`dropdown-arrow ${dropdownOpen ? "open" : ""}`}
              />
            </div>
            {dropdownOpen && (
              <div
                style={{
                  position: "fixed",
                  top: "50px",
                  right: "20px",
                  background: "white",
                  border: "1px solid #e5e7eb",
                  borderRadius: "10px",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                  minWidth: "160px",
                  zIndex: 99999,
                }}
              >
                <div
                  style={{
                    padding: "12px 16px",
                    cursor: "pointer",
                    borderBottom: "1px solid #f3f4f6",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    color: "#374151",
                  }}
                  onClick={handleProfilePage}
                >
                  <FaRegUser />
                  Profile
                </div>
                <div
                  style={{
                    padding: "12px 16px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    color: "#ef4444",
                  }}
                  onClick={handleLogoutClick}
                >
                  <MdLogout />
                  Logout
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <LogoutModal
        isOpen={showLogoutModal}
        onConfirm={confirmLogout}
        onClose={cancelLogout}
      />
    </header>
  );
};

export default Header;
